import { useWebPush } from "../hooks/useWebPush";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useParams } from "react-router";
import { Bell, CalendarDays, CircleDollarSign, Mail, PenLine, Plug, Settings2 } from "lucide-react";
import type { EmailEditorRef } from "@react-email/editor";
import RichEmailEditor from "../components/RichEmailEditor";
import {
    userSettings as userSettingsApi,
    emailSubscriptions as emailSubscriptionsApi,
    products as productsApi,
    notifications as notificationsApi,
    AUTH_ORIGIN,
    type EmailSubscription,
    type ProductKey,
    type SignatureRecord,
    type TipTapDoc,
    type NotificationPreferences,
} from "../lib/api";
import { getSelectedProduct, setSelectedProduct } from "../lib/prefs";
import GranolaSettings from "../components/loop/GranolaSettings";
import EmailProviderSettings from "../components/settings/EmailProviderSettings";
import SenderIdentitySettings from "../components/settings/SenderIdentitySettings";
import GmailSettings from "../components/settings/GmailSettings";
import AppearanceSettings from "../components/settings/AppearanceSettings";
import MeetingFeedSettings from "../components/settings/MeetingFeedSettings";
import { SelectBox, useConfirm } from "@/ui";

export const settingsQueryKey = (productKey: ProductKey | "unassigned") => ["userSettings", productKey] as const;
const unsubscribedQueryKey = (productKey: ProductKey | "unassigned") => ["email-subscriptions", "unsubscribed", productKey] as const;

const EMPTY_DOC: TipTapDoc = { type: "doc", content: [{ type: "paragraph", content: [] }] };

const SETTINGS_SECTIONS = [
    { id: "general", label: "General", icon: Settings2, description: "App-wide defaults" },
    { id: "mail", label: "Mail", icon: Mail, description: "Sending and subscriptions" },
    { id: "signatures", label: "Signatures", icon: PenLine, description: "Product email signatures" },
    { id: "sales", label: "Sales", icon: CircleDollarSign, description: "Pipeline and CRM" },
    { id: "calendar", label: "Calendar", icon: CalendarDays, description: "Calendar and scheduling setup" },
    { id: "notifications", label: "Notifications", icon: Bell, description: "Channels and categories" },
    { id: "integrations", label: "Integrations", icon: Plug, description: "Connected services" },
] as const;

type SettingsSection = typeof SETTINGS_SECTIONS[number]["id"];

function NotificationSettings() {
    const webPush = useWebPush(true);
    const [pushError, setPushError] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const query = useQuery({ queryKey: ["notification-preferences"], queryFn: notificationsApi.preferences });
    const update = useMutation({
        mutationFn: (patch: Partial<NotificationPreferences>) => notificationsApi.updatePreferences(patch),
        onSuccess: (result) => queryClient.setQueryData(["notification-preferences"], result),
    });
    const preferences = query.data?.preferences;
    const toggles: Array<{ key: keyof NotificationPreferences; label: string; detail: string }> = [
        { key: "inAppEnabled", label: "In-app notifications", detail: "Show notifications in the Kardoe workspace." },
        { key: "webPushEnabled", label: "Browser push", detail: "Send notifications when Kardoe is in the background." },
        { key: "nativeEnabled", label: "iPhone notifications", detail: "Send notifications to the signed-in iOS app." },
        { key: "mailEnabled", label: "Mail", detail: "New inbound mail and delivery activity." },
        { key: "taskEnabled", label: "Tasks", detail: "Task assignments and due-date activity." },
        { key: "contactEnabled", label: "Contacts and sales", detail: "CRM record and pipeline activity." },
        { key: "calendarEnabled", label: "Calendar", detail: "Calendar and booking activity." },
        { key: "meetingEnabled", label: "Meetings", detail: "Meeting reminders and room activity." },
        { key: "chatEnabled", label: "Chat and cases", detail: "Collaboration updates and case activity." },
    ];
    if (query.isLoading) return <div className="muted settings-card">Loading notification preferences…</div>;
    if (!preferences) return <div className="error settings-card">Notification preferences could not be loaded.</div>;
    async function enableEmailAlerts() {
        setPushError(null);
        if (!await webPush.subscribe()) {
            setPushError("Allow notifications in your browser’s site settings, then try again.");
            return;
        }
        update.mutate({ webPushEnabled: true, mailEnabled: true });
    }
    return <div className="settings-card settings-toggle-list">
        {webPush.supported && <div className="settings-toggle-row">
            <span><strong>Email alerts on this browser</strong><small>{webPush.state === "denied"
                ? "Notifications are blocked. Allow them in your browser’s site settings, then reload."
                : webPush.state === "subscribed" && preferences.webPushEnabled && preferences.mailEnabled
                    ? "This browser is registered for new email alerts."
                    : "Register this browser and allow notifications to receive new email alerts."}</small></span>
            <button type="button" disabled={webPush.state === "working" || webPush.state === "denied" || update.isPending}
                onClick={() => void enableEmailAlerts()}>
                {webPush.state === "working" ? "Enabling…" : "Enable email alerts"}
            </button>
        </div>}
        {(webPush.error || pushError) && <div className="error">{webPush.error || pushError}</div>}
        {toggles.map(toggle => <label className="settings-toggle-row" key={toggle.key}>
            <span><strong>{toggle.label}</strong><small>{toggle.detail}</small></span>
            <input
                type="checkbox"
                checked={Boolean(preferences[toggle.key])}
                disabled={update.isPending}
                onChange={event => update.mutate({ [toggle.key]: event.target.checked })}
            />
        </label>)}
        {update.error && <div className="error">{String((update.error as any).body?.detail ?? update.error.message)}</div>}
    </div>;
}

/** Compact "5 mins ago" / "2 days ago" / "Mar 14, 2026" formatter — used in
 *  the unsubscribed list. Falls back to the raw ISO string if Date parsing
 *  fails (shouldn't, but a server bug shouldn't break the UI). */
function formatRelativeOrDate(iso: string | undefined): string {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return iso;
    const diffMin = Math.round((Date.now() - t) / 60_000);
    if (diffMin < 1)         return "just now";
    if (diffMin < 60)        return `${diffMin}m ago`;
    if (diffMin < 60 * 24)   return `${Math.round(diffMin / 60)}h ago`;
    if (diffMin < 60 * 24 * 14) return `${Math.round(diffMin / (60 * 24))}d ago`;
    return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Detect "the editor is effectively empty" so we can store `null` instead of
 *  a single empty paragraph wrapper. Conservative: only the literal
 *  no-content shapes count as empty — anything with a child node is saved
 *  verbatim and the renderer-side `signatureNodes` is what decides whether
 *  to render the `-- ` delimiter. (Previous version walked content too
 *  aggressively and false-positived headings, marked text, etc.) */
function isEmptyDoc(doc: TipTapDoc | null | undefined): boolean {
    if (!doc || !Array.isArray(doc.content) || doc.content.length === 0) return true;
    // One-paragraph-with-no-children is what the editor shows when the
    // user has cleared it — also treat that as empty.
    if (doc.content.length === 1) {
        const only = doc.content[0] as any;
        if (only?.type === "paragraph" && (!only.content || only.content.length === 0)) {
            return true;
        }
    }
    return false;
}

export default function Settings() {
    const { section } = useParams<{ section?: string }>();
    const activeSection: SettingsSection = SETTINGS_SECTIONS.some(item => item.id === section)
        ? section as SettingsSection
        : "general";
    const activeMeta = SETTINGS_SECTIONS.find(item => item.id === activeSection)!;
    const queryClient = useQueryClient();
    const editorRef = useRef<EmailEditorRef>(null);
    const [saved, setSaved] = useState(false);
    const [productKey, setProductKey] = useState<ProductKey | "">(getSelectedProduct);
    // Local working copy of the signature library + which one is being edited.
    const [signatures, setSignatures] = useState<SignatureRecord[]>([]);
    const [selectedSigId, setSelectedSigId] = useState<string | null>(null);
    // Tracks which product we've seeded local state from, so refetches don't
    // clobber in-progress edits but switching products re-seeds.
    const loadedForRef = useRef<string | null>(null);

    const productsQuery = useQuery({
        queryKey: ["products"],
        queryFn:  () => productsApi.list(),
        staleTime: 5 * 60_000,
    });
    const productProfiles = productsQuery.data?.products ?? [];
    const productName = productProfiles.find(p => p.key === productKey)?.name ?? "Product";

    const { data, error: queryError, isLoading } = useQuery({
        queryKey: settingsQueryKey(productKey || "unassigned"),
        queryFn:  async () => {
            const selected = productKey as ProductKey;
            const res = await userSettingsApi.get(selected);
            return res.settings ?? { signature: null, signatures: [], productKey: selected };
        },
        enabled: Boolean(productKey),
    });
    const loadError = queryError
        ? String((queryError as any).body ?? (queryError as Error).message)
        : null;

    // Product-scoped suppressions, newest first.
    const unsubQuery = useQuery({
        queryKey: unsubscribedQueryKey(productKey || "unassigned"),
        queryFn:  async () => emailSubscriptionsApi.list({
            productKey: productKey as ProductKey,
            status:       "unsubscribed",
            limit:        "500",
            sort:         "-changedAt",
        }),
        staleTime: 30_000,
        enabled: Boolean(productKey),
    });
    const unsubscribed: EmailSubscription[] = unsubQuery.data?.data ?? [];

    /** Re-subscribe an address for this product. We don't bother with optimistic updates — the
     *  list is short and the round-trip is fast. Just refetch on success. */
    const confirm = useConfirm();
    const resubscribe = useMutation({
        mutationFn: (email: string) => {
            if (!productKey) throw new Error("Select a product");
            return emailSubscriptionsApi.set({
                productKey,
                email,
                status: "subscribed",
                reason: "manual",
            });
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: unsubscribedQueryKey(productKey || "unassigned") }),
    });

    // Seed the working library from the server once per product. A refetch
    // (same product) won't overwrite local edits; switching products re-seeds.
    useEffect(() => {
        if (!productKey || !data) return;
        if (loadedForRef.current === productKey) return;
        loadedForRef.current = productKey;
        const seeded: SignatureRecord[] = (data.signatures && data.signatures.length)
            ? data.signatures
            : (data.signature && !isEmptyDoc(data.signature)
                ? [{ id: "default", name: "Signature", content: data.signature, isDefault: true }]
                : []);
        setSignatures(seeded);
        setSelectedSigId(seeded.find(s => s.isDefault)?.id ?? seeded[0]?.id ?? null);
    }, [productKey, data]);

    const selectedSig = signatures.find(s => s.id === selectedSigId) ?? null;

    /** Merge the editor's current content back into the selected signature. */
    function withEditorContent(list: SignatureRecord[]): SignatureRecord[] {
        if (!selectedSigId) return list;
        const doc = editorRef.current?.getJSON() as TipTapDoc | undefined;
        const content = (!doc || isEmptyDoc(doc)) ? EMPTY_DOC : doc;
        return list.map(s => (s.id === selectedSigId ? { ...s, content } : s));
    }

    function selectSignature(id: string) {
        setSignatures(prev => withEditorContent(prev));
        setSelectedSigId(id);
    }

    function addSignature() {
        const id = crypto.randomUUID();
        setSignatures(prev => {
            const captured = withEditorContent(prev);
            return [...captured, {
                id,
                name: `Signature ${captured.length + 1}`,
                content: EMPTY_DOC,
                isDefault: captured.length === 0,
            }];
        });
        setSelectedSigId(id);
    }

    function renameSelected(name: string) {
        setSignatures(prev => prev.map(s => (s.id === selectedSigId ? { ...s, name } : s)));
    }

    function makeSelectedDefault() {
        setSignatures(prev => withEditorContent(prev).map(s => ({ ...s, isDefault: s.id === selectedSigId })));
    }

    function deleteSelected() {
        const remaining = signatures.filter(s => s.id !== selectedSigId);
        if (remaining.length && !remaining.some(s => s.isDefault)) {
            remaining[0] = { ...remaining[0], isDefault: true };
        }
        setSignatures(remaining);
        setSelectedSigId(remaining[0]?.id ?? null);
    }

    const save = useMutation({
        mutationFn: () => {
            if (!productKey) throw new Error("Select a product");
            return userSettingsApi.update({ productKey, signatures: withEditorContent(signatures) });
        },
        onSuccess: (res) => {
            if (res?.ok && res.settings) {
                queryClient.setQueryData(settingsQueryKey(productKey || "unassigned"), res.settings);
                setSignatures(res.settings.signatures ?? []);
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
            }
        },
    });

    /** Append a "Click to be removed from future emails" disclaimer paragraph
     *  to the end of the editor. Uses class `node-disclaimer` so:
     *   - the editor CSS shows it in small muted gray inside the composer,
     *   - sendFullWidth's `inlineDisclaimerStyle` translates it to an inline
     *     `style="…"` on outbound HTML (recipient clients have no access to
     *     our stylesheet), and
     *   - the `{{unsubscribe_url}}` placeholder href is rewritten per
     *     recipient to a signed `mail-in.../u/<token>` URL on send. */
    function insertUnsubscribeDisclaimer() {
        const ed: any = (editorRef.current as any)?.editor;
        if (!ed) return;
        const end = ed.state?.doc?.content?.size ?? 0;
        ed.chain()
            .focus()
            .insertContentAt(end, {
                type: "paragraph",
                attrs: { class: "node-disclaimer" },
                content: [
                    {
                        type: "text",
                        text: `Remove me from all future emails about ${productName}`,
                        marks: [{
                            type: "link",
                            attrs: {
                                href:   "{{unsubscribe_url}}",
                                target: "_blank",
                                rel:    "noopener noreferrer",
                                class:  "node-link",
                            },
                        }],
                    },
                ],
            })
            .run();
    }

    return (
        <div className="settings-shell">
            <aside className="settings-local-sidebar">
                <div className="settings-local-title"><Settings2 aria-hidden="true" /><span><strong>Settings</strong><small>All of Kardoe</small></span></div>
                <nav aria-label="Settings sections">
                    {SETTINGS_SECTIONS.map(item => {
                        const Icon = item.icon;
                        return <NavLink key={item.id} to={`/settings/${item.id}`} className={({ isActive }) => `settings-local-link${isActive ? " active" : ""}`}>
                            <Icon aria-hidden="true" />
                            <span><strong>{item.label}</strong><small>{item.description}</small></span>
                        </NavLink>;
                    })}
                </nav>
            </aside>
            <div className="settings-page">
            <header className="settings-section-header">
                <h2>{activeMeta.label}</h2>
                <p>{activeMeta.description}</p>
            </header>

            {loadError && <div className="error">{loadError}</div>}

            {(activeSection === "general" || activeSection === "mail" || activeSection === "signatures") && <div className="settings-card row" style={{ gap: 12 }}>
                <label htmlFor="settings-product">Product</label>
                <SelectBox
                    value={productKey}
                    className="w-auto"
                    style={{ minWidth: 220 }}
                    onValueChange={(v) => {
                        const next = v as ProductKey | "";
                        setProductKey(next);
                        setSelectedProduct(next);
                    }}
                    options={[
                        { value: "", label: "Select a product" },
                        ...productProfiles.map((product) => ({
                            value: product.key,
                            label: `${product.name}${product.configured ? "" : " (not configured)"}`,
                        })),
                    ]}
                />
            </div>}

            {activeSection === "general" && <div className="settings-card">
                <h3>Workspace defaults</h3>
                <p className="muted">The selected product becomes the default across mail, contacts, campaigns, and signatures on this device.</p>
                <div className="settings-link-grid">
                    <a href={`${AUTH_ORIGIN}/account/profile`}>Account profile <span>↗</span></a>
                    <Link to="/settings/notifications">Notification preferences <span>→</span></Link>
                    <Link to="/settings/integrations">Connected services <span>→</span></Link>
                </div>
            </div>}

            {activeSection === "general" && <AppearanceSettings />}

            {activeSection === "signatures" && <section className="settings-card">
                <h3>Email signatures</h3>
                <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    Defaults are added to new messages and forwards. Use the signature menu in replies.
                </p>

                {productKey && (
                    <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <SelectBox
                            value={selectedSigId ?? ""}
                            className="w-auto"
                            style={{ minWidth: 220 }}
                            disabled={signatures.length === 0}
                            onValueChange={(v) => selectSignature(v)}
                            options={signatures.length
                                ? signatures.map(s => ({ value: s.id, label: `${s.name}${s.isDefault ? "  ·  default" : ""}` }))
                                : [{ value: "", label: "No signatures yet" }]}
                        />
                        <button type="button" onClick={addSignature} disabled={isLoading}>New signature</button>
                        {selectedSig && (
                            <button type="button" onClick={deleteSelected} title="Delete this signature">Delete</button>
                        )}
                    </div>
                )}

                {selectedSig && (
                    <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <label htmlFor="sig-name" style={{ minWidth: 60 }}>Name</label>
                        <input
                            id="sig-name"
                            value={selectedSig.name}
                            onChange={(e) => renameSelected(e.target.value)}
                            style={{ flex: 1, minWidth: 200 }}
                        />
                        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, cursor: selectedSig.isDefault ? "default" : "pointer" }}>
                            <input
                                type="checkbox"
                                checked={selectedSig.isDefault}
                                disabled={selectedSig.isDefault}
                                onChange={() => makeSelectedDefault()}
                            />
                            Default
                        </label>
                    </div>
                )}

                <div className="compose-body-wrap" style={{ minHeight: 200, marginTop: 8 }}>
                    {isLoading && <div className="muted" style={{ padding: 12 }}>Loading…</div>}
                    {!isLoading && !selectedSig && (
                        <div className="muted" style={{ padding: 12 }}>
                            {productKey ? "No signatures yet." : "Select a product."}
                        </div>
                    )}
                    {!isLoading && selectedSig && (
                        // Re-key on the selected signature so the editor remounts
                        // with that signature's content when you switch.
                        <RichEmailEditor
                            ref={editorRef}
                            key={`${productKey}-${selectedSig.id}`}
                            content={(selectedSig.content ?? EMPTY_DOC) as any}
                        />
                    )}
                </div>

                <div className="row" style={{ gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                        className="primary"
                        onClick={() => save.mutate()}
                        disabled={!productKey || isLoading || save.isPending}
                    >
                        {save.isPending ? "Saving…" : "Save signatures"}
                    </button>
                    <button
                        type="button"
                        onClick={insertUnsubscribeDisclaimer}
                        disabled={!productKey || isLoading || !selectedSig}
                        style={{ fontSize: 13 }}
                        title="Insert a small footer with an unsubscribe link. The link is rewritten per recipient on send."
                    >
                        Add unsubscribe footer
                    </button>
                    {saved && <span className="muted" style={{ fontSize: 13 }}>Saved.</span>}
                    {save.error && (
                        <span className="error" style={{ fontSize: 13 }}>
                            {String((save.error as any).body ?? (save.error as Error).message)}
                        </span>
                    )}
                </div>
                <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
                    Links are signed per recipient and enable one-click unsubscribe in supported mail apps.
                </p>
            </section>}

            {activeSection === "mail" && <EmailProviderSettings />}
            {activeSection === "mail" && <SenderIdentitySettings />}

            {activeSection === "mail" && <section className="settings-card">
                <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    Unsubscribed contacts
                    <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
                        {unsubQuery.isLoading
                            ? "loading…"
                            : `${unsubscribed.length}${unsubscribed.length === 500 ? "+" : ""}`}
                    </span>
                </h3>
                <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    These contacts are skipped for {productName}. Only resubscribe someone who asked to be added again.
                </p>

                {unsubQuery.error && (
                    <div className="error" style={{ marginTop: 12 }}>
                        {String((unsubQuery.error as any).body?.error
                            ?? (unsubQuery.error as any).body
                            ?? (unsubQuery.error as Error).message)}
                    </div>
                )}

                <div className="list" style={{ marginTop: 8 }}>
                    {!unsubQuery.isLoading && unsubscribed.length === 0 && (
                        <div className="list-item muted">No unsubscribed contacts yet.</div>
                    )}
                    {unsubscribed.map((c) => (
                        <div key={c.id} className="list-item row" style={{ gap: 12, alignItems: "center" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {c.email}
                                </div>
                                <div className="muted" style={{ fontSize: 12 }}>{c.reason ?? "manual"}</div>
                            </div>
                            <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                {formatRelativeOrDate(c.changedAt)}
                            </div>
                            <button
                                onClick={async () => {
                                    if (await confirm({ title: `Re-subscribe ${c.email}?`, description: "Only do this if they explicitly asked.", destructive: false, confirmLabel: "Re-subscribe" })) {
                                        resubscribe.mutate(c.email);
                                    }
                                }}
                                disabled={resubscribe.isPending}
                                style={{ fontSize: 12 }}
                                title="Mark this contact as subscribed again"
                            >
                                Resubscribe
                            </button>
                        </div>
                    ))}
                </div>
                {resubscribe.error && (
                    <div className="error" style={{ marginTop: 8, fontSize: 13 }}>
                        Re-subscribe failed: {String((resubscribe.error as any).body?.error
                            ?? (resubscribe.error as any).body
                            ?? (resubscribe.error as Error).message)}
                    </div>
                )}
            </section>}

            {activeSection === "sales" && <div className="settings-card">
                <h3>Sales workspace</h3>
                <p className="muted">Manage the records and workflows that power the Sales focus area.</p>
                <div className="settings-link-grid">
                    <Link to="/pipeline">Pipeline stages and leads <span>→</span></Link>
                    <Link to="/contacts">Contacts <span>→</span></Link>
                    <Link to="/companies">Companies <span>→</span></Link>
                </div>
            </div>}

            {activeSection === "calendar" && <div className="settings-card">
                <h3>Calendar and scheduling</h3>
                <p className="muted">Configure availability, appointment types, and conflict calendars here. Daily calendar and meeting work lives in Workday.</p>
                <div className="settings-link-grid">
                    <Link to="/scheduling">Scheduling settings <span>→</span></Link>
                    <Link to="/meetings">Meetings <span>→</span></Link>
                    <Link to="/calendar">Calendar <span>→</span></Link>
                </div>
            </div>}
            {activeSection === "calendar" && <MeetingFeedSettings />}

            {activeSection === "notifications" && <NotificationSettings />}

            {activeSection === "integrations" && <div className="settings-card settings-integration-card"><GmailSettings /><GranolaSettings /></div>}
            </div>
        </div>
    );
}
